import { supabase } from '@/lib/supabase'
import { FALLBACK_CALENDAR_MAP } from '@/data/fallbackCalendar'

export interface Holiday {
  id: string
  date: string
  name: string
  type: 'holiday' | 'break' | 'special' | 'saturday' | 'camp'
  description?: string
  created_at: string
  updated_at: string
}

export interface DayInfo {
  holiday?: { name: string; type: Holiday['type'] }
  camp?: { name: string; type: 'camp' }
  tags: Array<{ name: string; type: Holiday['type'] | 'camp'; color: string }>
}

class HolidayService {
  private holidaysCache: Map<string, Holiday[]> = new Map()
  private lastFetch: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  /**
   * Fetch all holidays/camps from the database
   */
  async getHolidays(): Promise<Holiday[]> {
    const now = Date.now()
    
    if (this.holidaysCache.size > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
      const all: Holiday[] = []
      this.holidaysCache.forEach(items => all.push(...items))
      return all
    }

    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .order('date', { ascending: true })

      if (error) {
        console.error('Error fetching holidays from DB:', error)
        return []
      }

      this.holidaysCache.clear()
      data?.forEach((holiday: Holiday) => {
        const existing = this.holidaysCache.get(holiday.date) || []
        existing.push(holiday)
        this.holidaysCache.set(holiday.date, existing)
      })
      this.lastFetch = now

      return data || []
    } catch (error) {
      console.error('Exception fetching holidays:', error)
      return []
    }
  }

  /**
   * Get comprehensive day info (holidays, breaks, special days, camps) directly from DB records
   */
  async getDayInfo(dateStr: string): Promise<DayInfo | null> {
    let date: string
    if (dateStr.includes('.')) {
      const [day, month, year] = dateStr.split('.')
      date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    } else {
      date = dateStr
    }

    // Ensure cache is populated
    await this.getHolidays()

    const dayHolidays = this.holidaysCache.get(date)
    if (!dayHolidays || dayHolidays.length === 0) return null

    const tags: Array<{ name: string; type: Holiday['type'] | 'camp'; color: string }> = []
    let holiday: { name: string; type: Holiday['type'] } | undefined
    let camp: { name: string; type: 'camp' } | undefined

    for (const item of dayHolidays) {
      if (item.type === 'camp') {
        camp = { name: item.name, type: 'camp' }
        tags.push({
          name: item.name,
          type: 'camp',
          color: this.getTagColor('camp')
        })
      } else {
        holiday = { name: item.name, type: item.type }
        tags.push({
          name: item.name,
          type: item.type,
          color: this.getTagColor(item.type)
        })
      }
    }

    if (tags.length === 0) return null
    return { holiday, camp, tags }
  }

  async getHolidayInfo(dateStr: string): Promise<{ name: string; type: Holiday['type'] } | null> {
    const dayInfo = await this.getDayInfo(dateStr)
    return dayInfo?.holiday || null
  }

  private getTagColor(type: Holiday['type'] | 'camp'): string {
    const colors = {
      'holiday': 'bg-red-100 text-red-700 border-red-200',
      'break': 'bg-orange-100 text-orange-700 border-orange-200', 
      'special': 'bg-blue-100 text-blue-700 border-blue-200',
      'saturday': 'bg-purple-100 text-purple-700 border-purple-200',
      'camp': 'bg-green-100 text-green-700 border-green-200'
    }
    return colors[type] || 'bg-gray-100 text-gray-700 border-gray-200'
  }

  getDayInfoFallback(dateStr: string): DayInfo | null {
    let date: string
    if (dateStr.includes('.')) {
      const [day, month, year] = dateStr.split('.')
      date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    } else {
      date = dateStr
    }

    const item = FALLBACK_CALENDAR_MAP.get(date)
    if (!item) return null

    const tags: Array<{ name: string; type: Holiday['type'] | 'camp'; color: string }> = [
      {
        name: item.name,
        type: item.type,
        color: this.getTagColor(item.type)
      }
    ]

    const holiday = item.type !== 'camp' ? { name: item.name, type: item.type } : undefined
    const camp = item.type === 'camp' ? { name: item.name, type: 'camp' as const } : undefined

    return { holiday, camp, tags }
  }

  clearCache(): void {
    this.holidaysCache.clear()
    this.lastFetch = 0
  }
}

export const holidayService = new HolidayService()
